import * as React from 'react'
import {
  Typography,
  Button,
  Step,
  Box,
  StepContent,
  Stepper,
  StepButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { omit, set } from 'lodash'

import {
  CATEGORIES,
  ESTATE_SUB_CATEGORIES,
  COMMERCIAL_SUB_CATEGORIES,
  LAND_SUB_CATEGORIES,
  INTEREST_OPTIONS,
} from 'store/CreatePropertyAdSlice/constants'
import { useCreatePropertyAdStore } from 'store'
import shallow from 'zustand/shallow'
import {
  Control,
  UseFormTrigger,
  Path,
  useWatch,
  UseFormHandleSubmit,
  UseFormGetValues,
} from 'react-hook-form'
import { CheckCircle, Warning, Error } from '@mui/icons-material'
import { FormValues } from './'
import { TransitionProps } from '@mui/material/transitions'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { PropertyAdService } from 'api/services/PropertyAd'
import { propertyMap, PropertyAdTemplate } from './constants'
import { calculateAdCategoryId } from './helpers'
import { useSnackbar } from 'notistack'

import {
  AdminPropertyAd,
  Attachment,
  EstateAgent,
  PropertyAttribute,
  ValidationMessage,
} from 'types'
import KeycloakService from 'services/KeycloakService'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Grow ref={ref} {...props} />
})

interface ErrorDialogProps {
  open: boolean
  onClose: () => void
}
const ErrorDialog = (props: ErrorDialogProps) => {
  const { t } = useTranslation()
  const { open, onClose } = props
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle color="error">
        {t('manageEstateAd.validations.submit.title')}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ whiteSpace: 'pre' }}>
          {t('manageEstateAd.validations.submit.content')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('manageEstateAd.validations.submit.control')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface StepperStep {
  id: string
  label: string
  subLabel?: React.ReactNode
  description: string
}

interface Props {
  IS_IN_EDIT_MODE: boolean
  property?: AdminPropertyAd
  setLocalPropertyAd: (payload: AdminPropertyAd) => void
  setLocalValidationMessages: (messages: ValidationMessage[]) => void
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  handleSubmit: UseFormHandleSubmit<FormValues>
  getValues: UseFormGetValues<FormValues>
  agent?: EstateAgent
  // onUpdate: (propertyAd: AdminPropertyAd) => void
}

export default function PropertyStepper(props: Props) {
  const {
    property,
    control,
    trigger,
    getValues,
    IS_IN_EDIT_MODE,
    agent,
    setLocalPropertyAd,
    setLocalValidationMessages,
  } = props

  const { t } = useTranslation()

  const { enqueueSnackbar } = useSnackbar()

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const mutationCreatePropertyAd = PropertyAdService.hooks.useCreatePropertyAd({
    onSuccess: (data) => {
      setLocalPropertyAd(data)
      setLocalValidationMessages(data?.validationMessages ?? [])
      const HAS_ERROR = data?.validationMessages.some(
        (val) => val.validationType === 'ERROR'
      )
      if (HAS_ERROR) {
        enqueueSnackbar(t('manageEstateAd.messages.error'), {
          variant: 'error',
        })
      } else {
        enqueueSnackbar(t('manageEstateAd.messages.createSuccess'), {
          variant: 'success',
        })
        queryClient.invalidateQueries(PropertyAdService.keys.QueryKeys.all)
        queryClient.invalidateQueries(PropertyAdService.keys.QueryKeys.count)
        if (KeycloakService.isAdmin()) {
          navigate('/property-ads')
        }
        if (KeycloakService.isAgent()) {
          navigate('/my-property-ads')
        }
      }
    },
    onError: (err) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const mutationUpdatePropertyAd = PropertyAdService.hooks.useUpdatePropertyAd({
    onSuccess: (data) => {
      queryClient.setQueryData(['propertyAd', data.id], data)
      const HAS_ERROR = data?.validationMessages.some(
        (val) => val.validationType === 'ERROR'
      )
      if (HAS_ERROR) {
        enqueueSnackbar(t('manageEstateAd.messages.error'), {
          variant: 'error',
        })
      } else {
        enqueueSnackbar(t('manageEstateAd.messages.editSuccess'), {
          variant: 'success',
        })
        queryClient.invalidateQueries(PropertyAdService.keys.QueryKeys.all)
        queryClient.invalidateQueries(PropertyAdService.keys.QueryKeys.count)
        if (
          getValues('adPhotos.photos')?.find(
            (item) => item?.type !== 'attachment'
          ) &&
          IS_IN_EDIT_MODE
        ) {
          navigate(
            KeycloakService.isAgent() ? '/my-property-ads' : '/property-ads'
          )
        }
      }
    },
    onError: (err) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const watchCategory = useWatch({
    control,
    name: 'step1.category',
  })
  const watchSubCategory = useWatch({
    control,
    name: 'step1.subCategory',
  })
  const watchInterest = useWatch({
    control,
    name: 'adAttributes.interest',
  })

  const [
    currentStep,
    setStep,
    completedSteps,
    completeStep,
    uncompleteStep,
    validateStep,
    invalidateStep,
    invalidSteps,
    setStepInvalid,
    unsetStepInvalid,
  ] = useCreatePropertyAdStore(
    (state) => [
      state.currentStep,
      state.setStep,
      state.completedSteps,
      state.completeStep,
      state.uncompleteStep,
      state.validateStep,
      state.invalidateStep,
      state.invalidSteps,
      state.setStepInvalid,
      state.unsetStepInvalid,
    ],
    shallow
  )

  const [showErrorDialog, setShowErrorDialog] = React.useState(false)

  const handleCloseDialog = () => {
    setShowErrorDialog(false)
  }

  const currentCategory = React.useMemo(
    () => CATEGORIES.find((item) => item.value === watchCategory),
    [watchCategory]
  )
  const currentInterest = React.useMemo(
    () => INTEREST_OPTIONS.find((item) => item.value === watchInterest),
    [watchInterest]
  )

  const currentSubCategory = React.useMemo(() => {
    let CURRENT_OPTIONS
    switch (watchCategory) {
      case 'estate':
        CURRENT_OPTIONS = ESTATE_SUB_CATEGORIES
        break
      case 'commercial':
        CURRENT_OPTIONS = COMMERCIAL_SUB_CATEGORIES
        break
      case 'land':
        CURRENT_OPTIONS = LAND_SUB_CATEGORIES
        break
      default:
        CURRENT_OPTIONS = []
        break
    }
    return CURRENT_OPTIONS.find((item) => item.value === watchSubCategory)
  }, [watchCategory, watchSubCategory])

  const localSteps: StepperStep[] = [
    {
      id: 'category',
      label: t('manageEstateAd.steps.estateCategory.title'),
      subLabel: (
        <Typography variant="caption">
          {`${currentCategory ? t(currentCategory?.label) : ''} - ${t(
            currentSubCategory?.label
          )}`}
        </Typography>
      ),
      description: t('manageEstateAd.steps.estateCategory.description'),
    },
    {
      id: 'location',
      label: t('manageEstateAd.steps.estateLocation.title'),
      description: t('manageEstateAd.steps.estateLocation.description'),
    },
    {
      id: 'property',
      label: t('manageEstateAd.steps.estateAttributes.title'),
      description: t('manageEstateAd.steps.estateAttributes.description'),
    },
    {
      id: 'adPhotos',
      label: t('manageEstateAd.steps.estatePhotos.title'),
      description: t('manageEstateAd.steps.estatePhotos.description'),
    },
    {
      id: 'adAttributes',
      label: t('manageEstateAd.steps.adAttributes.title'),
      subLabel: (
        <Typography variant="caption">
          {`${currentInterest ? t(currentInterest?.label) : ''}`}
        </Typography>
      ),
      description: t('manageEstateAd.steps.adAttributes.description'),
    },
  ]

  const onSubmit = async () => {
    if (
      mutationCreatePropertyAd.isLoading ||
      mutationUpdatePropertyAd.isLoading
    ) {
      return
    }
    const steps: { path: string; id: string }[] = [
      { path: 'step1', id: 'category' },
      { path: 'location', id: 'location' },
      { path: 'property', id: 'property' },
      { path: 'adPhotos', id: 'adPhotos' },
      { path: 'adAttributes', id: 'adAttributes' },
    ]
    const tempStatus: number[] = []
    for (const [index, step] of steps.entries()) {
      let validStatus = await trigger(step.path as Path<FormValues>)
      if (step.path === 'property' && (!watchCategory || !watchSubCategory))
        validStatus = false
      if (validStatus) {
        completeStep(step.id)
        validateStep(step.id)
        unsetStepInvalid(step.id)
      } else {
        tempStatus.push(index)
        uncompleteStep(step.id)
        invalidateStep(step.id)
        setStepInvalid(step.id)
      }
    }
    if (tempStatus?.length) {
      setShowErrorDialog(true)
      return
    }

    const formValues = getValues()

    const generateExtrasProperties = (extras) => {
      const extrasTypes = {
        estate: {
          id: 7,
          description: 'ESTATE_PROPERTY_EXTRAS',
        },
        commercial: {
          id: 8,
          description: 'ESTATE_PROPERTY_COMMERCIAL_EXTRAS',
        },
        land: {
          id: 9,
          description: 'ESTATE_PROPERTY_LAND_TYPE_EXTRAS',
        },
      }
      let type: { id: number; description: string }
      if (formValues.step1.category === 'estate') {
        type = extrasTypes['estate']
      } else if (formValues.step1.category === 'commercial') {
        type = extrasTypes['commercial']
      } else {
        type = extrasTypes['land']
      }
      const parsedExtraProperties = Object.entries(extras)
        .filter((entry) => !!entry[1])
        .map((entry) => {
          return { id: null, type, valueText: entry[1] }
        })
      return parsedExtraProperties
    }

    const generatePropertyTypeAttribute = (step1) => {
      let propertyTypeAttribute: PropertyAttribute

      const PropertyTypes = {
        estate: {
          id: 1,
          description: 'ESTATE_PROPERTY_TYPE',
        },
        commercial: {
          id: 2,
          description: 'ESTATE_PROPERTY_COMMERCIAL_TYPE',
        },
        land: {
          id: 3,
          description: 'ESTATE_PROPERTY_LAND_TYPE',
        },
      }
      if (step1.category === 'estate') {
        propertyTypeAttribute = {
          type: PropertyTypes['estate'],
          valueText: step1.subCategory,
        }
      } else if (step1.category === 'commercial') {
        propertyTypeAttribute = {
          type: PropertyTypes['commercial'],
          valueText: step1.subCategory,
        }
      } else {
        propertyTypeAttribute = {
          type: PropertyTypes['land'],
          valueText: step1.subCategory,
        }
      }

      return propertyTypeAttribute
    }

    const generateArraySingleValueNumberProperties = () => {
      const values = JSON.parse(
        JSON.stringify(omit(formValues.property, ['extras', 'subCategory']))
      )
      const reversedMap = new Map(
        Array.from(propertyMap).map(([key, value]) => {
          return [value?.field, { property: key, ...value }]
        })
      )

      return Object.entries(values)
        .filter((entry) => entry[1] !== '')
        .reduce((acc, entry) => {
          const match = reversedMap.get(entry[0])
          if (match) {
            if (Array.isArray(entry[1])) {
              if (entry[1]?.length) {
                const mapped = entry[1].map((item) => {
                  return {
                    id: null,
                    type: {
                      description: match.property,
                      id: match.id,
                    },
                    valueText: match.type === 'TEXT' ? item : null,
                    valueNumber: match.type === 'NUMBER' ? item : null,
                  }
                })
                return [...acc, ...mapped]
              }
            } else {
              return [
                ...acc,
                {
                  id: null,
                  type: {
                    description: match.property,
                    id: match.id,
                  },
                  valueText: match.type === 'TEXT' ? entry[1] : null,
                  valueNumber: match.type === 'NUMBER' ? entry[1] : null,
                },
              ]
            }
          }
          return acc
        }, [])
    }

    const generateByteArrayImages = async (photos): Promise<Attachment[]> => {
      const parsedPhotos: Attachment[] = []

      for (let i = 0; i < photos?.length; i++) {
        if (photos[i]?.type === 'attachment') {
          parsedPhotos.push({ ...photos[i], orderNumber: i })
        } else {
          const ab = await photos[i].arrayBuffer()
          const ui8a = new Uint8Array(ab)
          const byteArray = Array.from(ui8a)

          const temp: Attachment = {
            id: null,
            alternativeId: null,
            externalId: null,
            dateTimeCreated: null,
            dateTimeUpdated: null,
            entityStatus: null,
            entityOwner: null,
            attachmentType: 'PHOTO',
            filePath: null,
            description: photos[i].description,
            fileName: photos[i].name,
            fileSize: photos[i].size,
            mimeType: photos[i].type,
            fileData: byteArray,
            orderNumber: i,
          }
          parsedPhotos.push(temp)
        }
      }
      return parsedPhotos
    }

    const attachments = await generateByteArrayImages(
      formValues.adPhotos.photos
    )

    const propertyTypeAttribute = generatePropertyTypeAttribute(
      formValues.step1
    )
    const extraProperties = generateExtrasProperties(formValues.property.extras)

    const restProperties = generateArraySingleValueNumberProperties()

    const preparePayload = () => {
      const payload = Object.assign(
        {},
        IS_IN_EDIT_MODE ? property : PropertyAdTemplate
      )
      const adCategoryId = calculateAdCategoryId(
        formValues.step1.category,
        formValues.adAttributes.interest
      )
      set(payload, 'attachments', attachments)
      set(payload, 'adCategory.id', adCategoryId)
      set(payload, 'descriptions', formValues.adAttributes.descriptions)
      set(payload, 'titles', formValues.adAttributes.titles)
      set(payload, 'externalId', formValues.adAttributes.estateCode)
      set(payload, 'salesPrice', formValues.adAttributes.salesPrice)
      set(payload, 'status', formValues.adAttributes.status)
      set(payload, 'properties[0].propertyAttributes', [
        propertyTypeAttribute,
        ...extraProperties,
        ...restProperties,
      ])

      set(
        payload,
        'properties[0].location.searchLocation',
        formValues.location.searchLocation
      )
      set(payload, 'properties[0].location.xcoordinate', formValues.location.x)
      set(payload, 'properties[0].location.ycoordinate', formValues.location.y)
      set(
        payload,
        'properties[0].location.addresses',
        formValues.location.addresses
      )
      set(
        payload,
        'properties[0].squareMeters',
        formValues.property.squareMeters
      )
      if ([1, 4, 2, 5].includes(adCategoryId)) {
        set(
          payload,
          'properties[0].constructionYear',
          formValues.property.constructionYear
        )
        set(payload, 'properties[0].propertyType', 'ESTATE')
      } else if ([3, 6].includes(adCategoryId)) {
        set(
          payload,
          'properties[0].coverageFactor',
          formValues.property.coverageFactor
        )
        set(
          payload,
          'properties[0].buildFactor',
          formValues.property.buildFactor
        )
        set(payload, 'properties[0].propertyType', 'LAND')
      }

      if (!IS_IN_EDIT_MODE && !!agent) {
        set(payload, 'estateAgent', JSON.parse(JSON.stringify(agent)))
      }
      if (agent?.cInfos?.length && formValues.adAttributes.agentCInfo) {
        const foundCInfo = agent?.cInfos.find(
          (cInfo) => cInfo?.id === formValues.adAttributes.agentCInfo
        )
        if (foundCInfo) {
          set(payload, 'agentCInfo', JSON.parse(JSON.stringify(foundCInfo)))
        }
      }
      set(payload, 'status', formValues.adAttributes.status)
      return payload
    }

    const propertyAdPayload = preparePayload()

    if (IS_IN_EDIT_MODE) {
      mutationUpdatePropertyAd.mutate({
        id: propertyAdPayload?.id,
        payload: propertyAdPayload,
      })
    } else {
      mutationCreatePropertyAd.mutate(propertyAdPayload as AdminPropertyAd)
    }
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 64 * 2,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', md: 400 },
          bgcolor: 'background.paper',
          py: 3,
          px: 2,
          borderRadius: 2.5,
        }}
      >
        <Stepper nonLinear activeStep={currentStep} orientation="vertical">
          {localSteps.map((step, index) => (
            <Step
              key={step.label}
              completed={completedSteps?.includes(step?.id)}
            >
              <StepButton
                onClick={() => setStep(index)}
                icon={
                  invalidSteps?.includes(step?.id) ? (
                    <Warning color="error" />
                  ) : completedSteps?.includes(step?.id) ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Error color="primary" />
                  )
                }
                optional={
                  invalidSteps?.includes(step?.id) ? (
                    <Typography color="textSecondary" variant="caption">
                      {t('manageEstateAd.hasErrors')}
                    </Typography>
                  ) : completedSteps?.includes(step?.id) ? (
                    step?.subLabel
                  ) : (
                    <Typography color="textSecondary" variant="caption">
                      {t('manageEstateAd.notCompleted')}
                    </Typography>
                  )
                }
              >
                {step.label}
              </StepButton>
              <StepContent>
                <Typography>{step.description}</Typography>
                <Box sx={{ mb: 2 }}></Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Button
          fullWidth
          variant="contained"
          onClick={onSubmit}
          sx={{
            mt: 1,
            mr: 1,
            py: 2,
            fontSize: 20,
            minHeight: 70,
            minWidth: '140px',
            pointerEvents:
              mutationCreatePropertyAd.isLoading ||
              mutationUpdatePropertyAd.isLoading
                ? 'none'
                : 'auto',
          }}
        >
          {mutationCreatePropertyAd.isLoading ||
          mutationUpdatePropertyAd.isLoading ? (
            <CircularProgress style={{ color: 'white' }} />
          ) : IS_IN_EDIT_MODE ? (
            t('manageEstateAd.update')
          ) : (
            t('manageEstateAd.submit')
          )}
        </Button>
      </Box>
      <ErrorDialog open={showErrorDialog} onClose={handleCloseDialog} />
    </Box>
  )
}
